import { type FormEvent, useRef, useState } from "react";
import { getRunSettings, PACKAGE_CDN, useRunSettings } from "../run/run-settings";
import { closeOnBackdrop } from "./dialog";
import { Icon } from "./Icon";

interface RunOptionsProps {
  /** The runner for the open file, if it has one. */
  runner: { id: string; name: string } | undefined;
}

/** Per-runner program arguments, kept on this device. */
export function RunOptions({ runner }: RunOptionsProps) {
  const settings = useRunSettings();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [line, setLine] = useState("");
  const [packages, setPackages] = useState(false);
  // Only the JavaScript runner can load npm packages (from esm.sh).
  const supportsPackages = runner?.id === "javascript";
  const current = runner ? settings.args[runner.id] : undefined;

  const open = () => {
    setLine(current ?? "");
    setPackages(runner ? getRunSettings().packagesAllowed(runner.id) : false);
    dialogRef.current?.showModal();
  };

  const save = (event: FormEvent) => {
    event.preventDefault();
    if (runner) {
      getRunSettings().setArgs(runner.id, line);
      if (supportsPackages) getRunSettings().setPackagesAllowed(runner.id, packages);
    }
    dialogRef.current?.close();
  };

  return (
    <>
      <button
        type="button"
        className="action-button run-options-button"
        aria-label="Run options"
        title={current ? `Arguments: ${current}` : "Run options (program arguments)"}
        disabled={!runner}
        onClick={open}
      >
        <Icon name="sliders" size={16} />
      </button>
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: Escape closes dialogs natively; this only adds backdrop clicks for the mouse */}
      <dialog ref={dialogRef} onClick={closeOnBackdrop} className="dialog" aria-label="Run options">
        {runner && (
          <form onSubmit={save}>
            <h2>Run options</h2>
            <label>
              Arguments for {runner.name} programs
              <input
                value={line}
                autoFocus
                spellCheck={false}
                placeholder="e.g. --verbose 'two words'"
                onChange={(e) => setLine(e.target.value)}
              />
            </label>
            {supportsPackages && (
              <label className="checkbox">
                <input
                  type="checkbox"
                  checked={packages}
                  onChange={(e) => setPackages(e.target.checked)}
                />
                Allow npm packages (<code>import x from "lodash"</code> loads from {PACKAGE_CDN})
              </label>
            )}
            <p className="dialog-hint">
              Passed to every {runner.name} program you run, like a shell would (quotes group
              words). Saved on this device only: runs are always local, and collaborators can't
              start them.
            </p>
            <div className="dialog-actions">
              <button type="button" onClick={() => dialogRef.current?.close()}>
                Cancel
              </button>
              <button type="submit" className="primary">
                Save
              </button>
            </div>
          </form>
        )}
      </dialog>
    </>
  );
}
